/**
 * Topf einer Kasse an diesem Tag, in der Kassentabelle des Betriebstags.
 *
 * Die Vorgabe kommt aus den Stammdaten. An einzelnen Abenden läuft es anders:
 * getrennte Veranstaltungen, ein Gerät aus dem Yuca an der CBE-Garderobe.
 * Die Auswahl gilt sofort und nur für diesen Tag, der Tag wird neu gerechnet.
 * Eine Abweichung ist gelb umrandet und nennt die Vorgabe; zurück geht es über
 * dieselbe Auswahl. Gewünscht war (Oktober 2026): schnell umstellen und
 * vergleichen, ohne Panel und ohne Zwischenschritt.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type PoolDto, type WorkstationDayDto } from "../lib/api";
import { topfAuswahl } from "../lib/topf";

export function TopfWahl({
  date,
  zeile,
  pools,
  gesperrt,
  offen,
}: {
  date: string;
  zeile: WorkstationDayDto;
  pools: readonly PoolDto[];
  gesperrt: boolean;
  /** An der Kasse ist noch etwas zu entscheiden. */
  offen: boolean;
}) {
  const queryClient = useQueryClient();
  const umstellen = useMutation({
    mutationFn: (poolId: string) =>
      api.put(`/business-days/${date}/kassen/${zeile.workstationId}/topf`, { poolId }),
    onSettled: () => queryClient.invalidateQueries(),
  });

  const aktuell = zeile.topf;
  const abweichend = aktuell !== null && aktuell.id !== zeile.topfVorgabe?.id;
  const gesperrtWeil = gesperrt
    ? "Der Tag ist gesperrt, also ausgezahlt."
    : offen
      ? "An dieser Kasse ist noch etwas zu entscheiden. Erst das klären, dann den Topf wählen."
      : null;

  return (
    // Ein Klick in die Auswahl soll die Zeile nicht auf- und zuklappen.
    <div onClick={(e) => e.stopPropagation()}>
      <select
        className={`field w-auto py-1 ${abweichend ? "border-warn bg-warn/5" : ""}`}
        value={aktuell?.id ?? ""}
        disabled={gesperrtWeil !== null || umstellen.isPending}
        onChange={(e) => {
          // Fokus abgeben: sonst gehören die Pfeiltasten danach der Auswahl —
          // kein Blättern, und unter Windows wählten sie gleich den nächsten Topf.
          e.currentTarget.blur();
          umstellen.mutate(e.target.value);
        }}
        aria-label={`Topf von ${zeile.workstationName}`}
        title={
          gesperrtWeil ??
          (abweichend
            ? `Nur an diesem Tag. Laut Stammdaten ${zeile.topfVorgabe?.name ?? "ohne Topf"}.`
            : "Gilt nur für diesen Tag; der Tag wird danach neu gerechnet.")
        }
      >
        {aktuell === null ? (
          <option value="" disabled>
            kein Topf
          </option>
        ) : null}
        {topfAuswahl(pools, date, aktuell).map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      {umstellen.isPending ? (
        <div className="mt-0.5 text-xs text-muted">rechnet neu …</div>
      ) : abweichend ? (
        <div className="mt-0.5 text-xs text-muted">statt {zeile.topfVorgabe?.name ?? "ohne Topf"}</div>
      ) : null}
      {umstellen.error ? (
        <div className="mt-0.5 max-w-[14rem] text-xs text-alert">{umstellen.error.message}</div>
      ) : null}
    </div>
  );
}
